// Position details: what you hold, since when, the purchases it is made of,
// and three separate actions — «＋ Добавить покупку» (a real extra buy: the
// editor re-weights the average), «Изменить позицию» (correct a purchase entered
// by mistake), «Удалить позицию» (the coin stays in «Мои монеты»).
import i18n from '@/i18n';
import { useCaps } from '@/hooks/useCaps';
import { useState } from 'react';
import { Link } from 'react-router';
import { CoinIcon } from '@/components/ui/CoinIcon';
import { Icon } from '@/components/ui/Icon';
import { Spark } from '@/components/ui/Indicators';
import { useSheet } from '@/components/ui/Sheet';
import { positionCtx } from '@/features/ai/contexts';
import { AskOrca } from '@/features/ai/OrcaAnswer';
import { useAskContext } from '@/features/ai/useAskContext';
import { usePortfolio } from '@/hooks/derived';
import { useMarket } from '@/hooks/queries';
import { useTime } from '@/hooks/useTime';
import { cx, dir, nf, pct, qty, susd, usd } from '@/lib/format';
import { daysBetween, lotDateLabel, sinceLabel } from '@/lib/time';
import { lotKey } from '@/lib/position';
import { MOCK_DATA } from '@/lib/api/mode';
import type { PurchaseLot } from '@/types';
import { LotForm, usePositionActions, usePositionEditor } from './PositionEditor';

/** Entry points: add (a new position or one more purchase — the same editor) and details. */
export function useOpenPosition() {
  const { openSheet } = useSheet();
  const edit = usePositionEditor();
  return {
    add: (symbol?: string) => edit(symbol),
    detail: (symbol: string) => openSheet({ kicker: i18n.t('portfolio:pos.kicker'), title: symbol, body: <PositionDetail symbol={symbol} />, className: 'pf-sheet' }),
  };
}

/** «22 дня» / «3 мес.» / «1,2 г.» */
const heldFor = (d: number) => (d < 1 ? i18n.t('portfolio:pos.fromToday') : d < 60 ? i18n.t('common:unit.day', { count: d }) : d < 365 ? i18n.t('portfolio:pos.months', { n: Math.round(d / 30) }) : i18n.t('portfolio:pos.years', { n: nf(d / 365, 1) }));
/** a purchase's date; a day found by price history is marked «≈ … · по истории цены» */
const lotDate = (l: PurchaseLot, today: string) => (l.date === today && l.dateSource === 'user' ? i18n.t('common:time.today') : lotDateLabel(l));
const LotWhen = ({ l, today }: { l: PurchaseLot; today: string }) => (
  <span className={cx('lot-d', l.dateSource === 'history' && 'est', !l.date && !l.period && 'none')}>{lotDate(l, today)}{l.dateSource === 'history' && <small>{i18n.t('portfolio:lot.byHistory')}</small>}</span>
);

/** the purchase price: yours «$80 000», Orca's estimate «≈ $2 430 · оценка по истории рынка», or unknown */
const LotPrice = ({ l, decimals }: { l: PurchaseLot; decimals: number }) => (
  l.price === undefined ? <span className="lot-p none">{i18n.t('portfolio:table.priceUnknown')}</span>
    : l.priceSource && l.priceSource !== 'user'
      ? <span className="lot-p est">≈ {usd(l.price, decimals)}<small>{i18n.t('portfolio:lot.estimate')}</small></span>
      : <span className="lot-p">{usd(l.price, decimals)}</span>
);

export function PositionDetail({ symbol }: { symbol: string }) {
  const pf = usePortfolio();
  const { watchlist, universe } = useMarket();
  const { updateLot, removeLot, removePosition } = usePositionActions();
  const editor = usePositionEditor();
  const { closeSheet, isOpen } = useSheet();
  const caps = useCaps();
  const tf = useTime();
  const r = pf.rows.find((x) => x.symbol === symbol);
  const [mode, setMode] = useState<'view' | 'edit' | 'delete'>('view');
  const [fixing, setFixing] = useState<string | null>(null);
  const [showLots, setShowLots] = useState<boolean | null>(null);
  useAskContext(isOpen && r ? positionCtx(r) : null, 3);
  if (!r) return <p className="empty">{i18n.t('portfolio:pos.removed', { coin: symbol })}</p>;
  const coin = universe.find((c) => c.symbol === symbol)!;
  const watched = watchlist.some((c) => c.symbol === symbol);
  const today = tf.todayKey();
  const lots = [...r.lots].sort((a, b) => (lotKey(a) ?? '9999').localeCompare(lotKey(b) ?? '9999'));
  const open = showLots ?? lots.length > 1;
  const one = lots.length === 1;
  const ax = r.estimated ? '≈ ' : '';

  return (
    <div className="pfd">
      <div className="pfd-top">
        <span className="pfd-ic"><CoinIcon symbol={symbol} /></span>
        <div><b>{r.unpriced ? '—' : usd(r.value, 2)}</b><span>{coin.name} · {i18n.t('common:unit.piece', { qty: qty(r.qty) })}{r.unpriced ? '' : ` · ${i18n.t('portfolio:pos.share', { v: nf(r.share, 1) })}`}</span></div>
        {MOCK_DATA && <Spark seed={coin.sparkSeed} trend={coin.change24h / 3} change={coin.change24h} />}
      </div>
      <p className={cx('pfd-since', !r.since && 'none')}>
        <Icon name="cal" />
        {r.since
          ? <span>{i18n.t('portfolio:pos.inPosition', { since: sinceLabel(r.since, r.sinceSource) })}{r.sinceSource === 'history' ? ` · ${i18n.t('portfolio:lot.byHistory')}` : r.sinceSource === 'user' ? ` · ${heldFor(daysBetween(r.since, today))}` : ''}</span>
          : <span>{i18n.t('portfolio:pos.noDate')}</span>}
      </p>
      {/* estimated prices make the aggregate approximate («≈»); purchases without a price leave P/L uncalculated */}
      <div className="pfd-stats">
        <div><span>{i18n.t('portfolio:pos.avg')}</span><b>{r.pricedQty ? `${ax}${usd(r.avgPrice, r.decimals)}` : '—'}</b>{r.incomplete && r.pricedQty > 0 && <small className="muted">{i18n.t('portfolio:pos.pricedOnly')}</small>}</div>
        <div><span>{i18n.t('portfolio:pos.current')}</span><b>{r.unpriced ? '—' : usd(r.price, r.decimals)}</b></div>
        <div><span>{i18n.t('portfolio:pos.invested')}</span><b>{r.incomplete ? (r.pricedQty ? `${ax}${usd(r.cost, 0)} + ?` : '—') : `${ax}${usd(r.cost, 0)}`}</b></div>
        <div><span>{i18n.t('portfolio:table.pnl')}</span>{r.incomplete || r.unpriced ? <b>—</b> : <b className={dir(r.pnl)}>{ax}{susd(r.pnl, 0)}<small>{ax}{pct(r.pnlPct, 1)}</small></b>}</div>
        <div><span>{i18n.t('portfolio:pos.d24')}</span><b className={dir(r.change24h)}>{pct(r.change24h, 1)}</b></div>
      </div>
      {r.incomplete ? (
        <p className="pfd-note"><Icon name="alert" /><span>{r.pricedQty ? i18n.t('portfolio:pos.partialPrice', { coin: symbol }) : i18n.t('portfolio:pos.noPrice', { coin: symbol })}{' '}
          <button type="button" className="hist-link inl" onClick={() => { setMode('edit'); setFixing(lots.find((l) => l.price === undefined)?.id ?? null); }}>{i18n.t('portfolio:pos.fixPrice')}</button></span></p>
      ) : r.estimated && <p className="pfd-note quiet"><span>{i18n.t('portfolio:pos.estimatedNote')}</span></p>}

      {mode === 'view' && (
        <div className="pfd-acts">
          <button type="button" className="buy" onClick={() => editor(symbol)}><Icon name="plus" />{i18n.t('portfolio:addPurchase')}</button>
          <button type="button" onClick={() => { setMode('edit'); setFixing(one ? lots[0].id : null); }}><Icon name="gear" />{i18n.t('portfolio:pos.edit')}</button>
          <button type="button" className="danger" onClick={() => setMode('delete')}><Icon name="trash" />{i18n.t('portfolio:pos.delete')}</button>
        </div>
      )}

      {mode === 'edit' && (
        <div className="pfd-edit">
          <p className="pfd-edit-h"><b>{i18n.t('portfolio:pos.fixTitle')}</b>{i18n.t('portfolio:pos.fixText')}</p>
          <ul className="lots edit">
            {lots.map((l) => (
              <li key={l.id} className={cx(fixing === l.id && 'open')}>
                {fixing === l.id ? (
                  <LotForm coin={coin} initial={l} submitLabel={i18n.t('common:action.save')} autoFocus={false} onCancel={() => (one ? setMode('view') : setFixing(null))}
                    onSubmit={(lot) => { void updateLot(symbol, l.id, lot, i18n.t('portfolio:toast.lotFixed', { coin: symbol })); setFixing(null); setMode('view'); }} />
                ) : (
                  <div className="lot">
                    <LotWhen l={l} today={today} /><b>{qty(l.qty)} {symbol}{l.origin === 'telegram_position' && <em className="pf-off">Telegram</em>}</b><LotPrice l={l} decimals={coin.decimals} />
                    <span className="lot-acts">
                      <button type="button" onClick={() => setFixing(l.id)}>{i18n.t('portfolio:pos.fix')}</button>
                      {lots.length > 1 && <button type="button" className="danger" onClick={() => { void removeLot(symbol, l.id, i18n.t('portfolio:toast.lotRemoved', { coin: symbol })); }}>{i18n.t('common:action.delete')}</button>}
                    </span>
                  </div>
                )}
              </li>
            ))}
          </ul>
          {!fixing && <button type="button" className="stc-link" onClick={() => setMode('view')}>{i18n.t('common:action.done')}</button>}
        </div>
      )}
      {mode === 'delete' && (
        <div className="pfd-del" role="alertdialog" aria-label={i18n.t('portfolio:pos.delete')}>
          <p><b>{i18n.t('portfolio:pos.deleteQ', { coin: symbol })}</b>{i18n.t('portfolio:pos.deleteText', { coin: symbol, count: lots.length })}</p>
          <div className="pfs-acts">
            <button type="button" className="pfd-danger" onClick={() => { void removePosition(symbol, i18n.t('portfolio:toast.posRemoved', { coin: symbol })); closeSheet(); }}><Icon name="trash" />{i18n.t('portfolio:pos.delete')}</button>
            <button type="button" className="stc-link" onClick={() => setMode('view')}>{i18n.t('common:action.cancel')}</button>
          </div>
        </div>
      )}

      {mode !== 'edit' && (
        <div className={cx('pfd-lots', open && 'open')}>
          <button type="button" className="pfd-lots-h" aria-expanded={open} onClick={() => setShowLots(!open)}>
            <span>{i18n.t('portfolio:lots.title', { n: lots.length })}</span><small>{open ? i18n.t('portfolio:lots.collapse') : i18n.t('portfolio:lots.where')}</small><Icon name="chev" />
          </button>
          {open && (
            <>
              <ul className="lots">
                {lots.map((l) => <li key={l.id}><div className="lot"><LotWhen l={l} today={today} /><b>{qty(l.qty)} {symbol}</b><LotPrice l={l} decimals={coin.decimals} /></div></li>)}
              </ul>
              <div className="lots-sum"><span>{i18n.t('portfolio:lots.total')}</span><b>{qty(r.qty)} {symbol}</b><span>{i18n.t('portfolio:lots.avg', { v: r.pricedQty ? `${ax}${usd(r.avgPrice, r.decimals)}` : '—' })}</span></div>
            </>
          )}
        </div>
      )}

      <div className="pfd-links">
        {watched
          ? <Link to={`/signals?coin=${symbol}`} onClick={closeSheet}><Icon name="link" /><span><b>{i18n.t('portfolio:links.signals', { coin: symbol })}</b><small>{caps.can('weekly_forecast') ? i18n.t('portfolio:links.signalsSub') : i18n.t('portfolio:links.signalsSubFree')}</small></span><Icon name="chev" /></Link>
          : <Link to="/settings#coins" onClick={closeSheet}><Icon name="link" /><span><b>{i18n.t('portfolio:links.track', { coin: symbol })}</b><small>{i18n.t('portfolio:links.trackSub')}</small></span><Icon name="chev" /></Link>}
        <Link to={`/news?coin=${symbol}&scope=week`} onClick={closeSheet}><Icon name="news" /><span><b>{i18n.t('portfolio:links.news', { coin: symbol })}</b><small>{i18n.t('portfolio:links.newsSub')}</small></span><Icon name="chev" /></Link>
      </div>
      <div className="sh-actions"><AskOrca ctx={positionCtx(r)} /><span className="sh-hint">{i18n.t('portfolio:pos.askHint')}</span></div>
    </div>
  );
}
